import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { SharedService } from './shared.service';
import { TranslationApiService } from './translation.api.service';

@Injectable({ providedIn: 'root' })
export class BackendTranslationService {
  private api = inject(TranslationApiService);
  private translate = inject(TranslateService);
  private sharedService = inject(SharedService);

  private loadedScopes = new Set<string>();

  private eagerScopes: string[] = [];
  private etags = new Map<string, string>(); // key = lang:scope

  async init(lang: string): Promise<void> {
    try {
      const manifest = await firstValueFrom(this.api.getManifest('WEB', this.sharedService.kiIndustryTypeOrDefault, lang));
      const scopes = manifest.scopes ?? [];

      this.eagerScopes = scopes.filter(s => s.eager).map(s => s.scope);
    } catch (err) {
      console.warn('Translation manifest unavailable', err);
      this.eagerScopes = [];
    }
  }

  getEagerScopes(): string[] {
    return this.eagerScopes;
  }

  async loadScope(lang: string, scope: string): Promise<void> {
    const key = `${lang}:${scope}`;

    if (!scope || this.loadedScopes.has(key)) {
      return;
    }

    try {
      const response = await firstValueFrom(
        this.api.getScope(
          'WEB',
          this.sharedService.kiIndustryTypeOrDefault,
          lang,
          scope,
          undefined, // WEB has no clientVersion
          this.etags.get(key)
        )
      );

      // 200 → new/updated translation
      if (response.status === 200 && response.body?.data) {
        this.translate.setTranslation(lang, response.body.data, true);

        const etag = response.headers.get('ETag');
        if (etag) {
          this.etags.set(key, etag); // includes quotes
        }
      }

      this.loadedScopes.add(key);
    } catch (err: any) {
      // 304 will land here in Angular
      if (err.status === 304) {
        this.loadedScopes.add(key);
        return;
      }

      if (err.status === 401 || err.status === 403) {
        console.error(`Translation scope permanently blocked: ${scope}`);
        this.loadedScopes.add(key);
        return;
      }

      console.warn(`Temporary failure loading scope ${scope}`, err);
    }
  }

  // async loadScope(lang: string, scope: string): Promise<void> {
  //   const key = `${lang}:${scope}`;

  //   if (!scope || this.loadedScopes.has(key)) {
  //     return;
  //   }

  //   try {
  //     const response = await firstValueFrom(
  //       this.api.getScope(lang, scope, this.etags.get(key))
  //     );

  //     if (response.status === 200 && response.body?.data) {
  //       this.translate.setTranslation(lang, response.body.data, true);

  //       const etag = response.headers.get('ETag');
  //       if (etag) { this.etags.set(key, etag) };
  //     }

  //     this.loadedScopes.add(key);

  //   } catch (err: any) {

  //     if (err.status === 401 || err.status === 403) {
  //       console.error(`Translation scope permanently blocked: ${scope}`);
  //       this.loadedScopes.add(key); // 🔑 STOP RETRIES
  //       return;
  //     }

  //     console.warn(`Temporary failure loading scope ${scope}`, err);
  //   }
  // }

  clearLanguage(lang: string): void {
    this.loadedScopes.forEach(key => {
      if (key.startsWith(lang + ':')) {
        this.loadedScopes.delete(key);
      }
    });
  }
}
